/**
 * API Client for Tenant Mgmt Lambda Backend
 *
 * Communicates with the Lambda API at {stage|prod}/api/tenant-mgmt/{route}.
 * - Loaders: GET requests for page-specific data
 * - Actions: POST requests for business operations (mutations)
 * - sendIntent: POST to /action with intent name (e.g. _add_service_request)
 *
 * Base client from ~/lib/api-client (mock registry when NODE_ENV=test).
 */

import { createBaseApiClient } from "~/lib/api-client/createBaseApiClient";
import * as APITypes from "~/API";
import {
  type _AdvanceLeaseActionType,
  type _CancelLeaseActionType,
} from "~/types/lease";
import { ENVS, getAuthWrapper } from "~/utils";

// ============================================================================
// Types
// ============================================================================

export interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  error?: string;
  debugLogs?: string[];
}

export interface TenantContext {
  tenantID: string;
}

// ============================================================================
// Base client (mock registry when NODE_ENV=test)
// ============================================================================

const base = createBaseApiClient({
  baseUrl: () => ENVS.lambda_api_url,
  getAuthToken: async () => {
    try {
      return await getAuthWrapper().currentUser?.getIdToken();
    } catch {
      return undefined;
    }
  },
});

/**
 * Parallel Vitest forks share one Firebase uid but AppSync Tenant ids may be
 * `…-testworker-${poolId}`. Lambda `pickTenantForAuthUID` needs the pool hint in
 * test only — never sent in production (NODE_ENV !== "test").
 */
function vitestTenantIsolationPoolId(): string | undefined {
  if (typeof process === "undefined" || process.env.NODE_ENV !== "test") {
    return undefined;
  }
  const raw = process.env.VITEST_POOL_ID ?? process.env.VITEST_WORKER_ID;
  if (raw == null || String(raw).length === 0) return undefined;
  return String(raw);
}

// ============================================================================
// API Client
// ============================================================================

export const apiClient = {
  async loader<T>(
    loaderPath: string,
    params?: Record<string, string | undefined>
  ): Promise<ApiResponse<T>> {
    const pool = vitestTenantIsolationPoolId();
    const merged: Record<string, string | undefined> = {
      ...(params ?? {}),
      ...(pool ? { tenantIsolationPoolId: pool } : {}),
    };
    const filteredParams = Object.fromEntries(
      Object.entries(merged).filter(([_, v]) => v !== undefined),
    );

    const queryString =
      Object.keys(filteredParams).length > 0
        ? "?" +
          new URLSearchParams(filteredParams as Record<string, string>).toString()
        : "";

    return base.request<T>(`/loader/${loaderPath}${queryString}`, "GET");
  },

  /**
   * Public loader (no Authorization) — e.g. shadow-invite preview with signed token.
   */
  async loaderPublic<T>(
    loaderPath: string,
    params?: Record<string, string | undefined>
  ): Promise<ApiResponse<T>> {
    const filteredParams = params
      ? Object.fromEntries(
          Object.entries(params).filter(([_, v]) => v !== undefined)
        )
      : {};
    const queryString =
      Object.keys(filteredParams).length > 0
        ? "?" +
          new URLSearchParams(filteredParams as Record<string, string>).toString()
        : "";
    return base.requestPublic<T>(`/loader/${loaderPath}${queryString}`, "GET");
  },

  async action<T>(
    actionName: string,
    payload: Record<string, unknown>
  ): Promise<ApiResponse<T>> {
    const auth = getAuthWrapper();
    const user = auth.currentUser;

    if (!user) {
      return { success: false, data: null, error: "Not authenticated" };
    }

    const body = {
      action: actionName,
      tenantContext: undefined as TenantContext | undefined,
      userName: user.displayName || user.email || undefined,
      ...payload,
    };

    return base.request<T>("/action", "POST", body);
  },

  async sendIntent<T>(
    intentName: string,
    payload: Record<string, unknown>,
    tenantContext?: TenantContext
  ): Promise<ApiResponse<T>> {
    const auth = getAuthWrapper();
    const user = auth.currentUser;

    if (!user) {
      return { success: false, data: null, error: "Not authenticated" };
    }

    const pool = vitestTenantIsolationPoolId();
    const body = {
      intent: intentName,
      tenantContext,
      userName: user.displayName || user.email || undefined,
      ...payload,
      ...(pool ? { tenantIsolationPoolId: pool } : {}),
    };

    return base.request<T>("/action", "POST", body);
  },

  /** Test helpers: mock response/failure for intent/loader; clear all mocks */
  setMock: base.setMock.bind(base),
  setMockFailure: base.setMockFailure.bind(base),
  clearMocks: base.clearMocks.bind(base),
};

// ============================================================================
// Loader Result Types (match Lambda tenant-mgmt loaders)
// ============================================================================

/** Unit item in building details: unit + building + property context */
export interface BuildingDetailsUnitItem {
  unitDetails: APITypes.Unit;
  buildingDetails: APITypes.Building;
  propertyDetails: APITypes.Property;
}

/** building-details loader response */
export interface BuildingDetailsLoaderResult {
  buildingDetails: APITypes.Building;
  propertyDetails: APITypes.Property;
  unitList: BuildingDetailsUnitItem[];
  leases: APITypes.Lease[];
}

/** service-request-details loader response */
export interface ServiceRequestDetailsLoaderResult {
  serviceRequest: APITypes.ServiceRequest;
}

/** service-request-lease-options loader (tenant add maintenance request) */
export interface ServiceRequestLeaseOptionItem {
  leaseId: string;
  label: string;
}

export interface AddServiceRequestLeaseOptionsLoaderResult {
  leaseOptions: ServiceRequestLeaseOptionItem[];
  defaultLeaseId: string | null;
}

/** lease-table loader response (tenant lease list by application status) */
export interface LeaseListLoaderResult {
  leaseListApproved: LeaseListLoaderResultItem[];
  leaseListUnderReview: LeaseListLoaderResultItem[];
  leaseListSubmitted: LeaseListLoaderResultItem[];
}

export interface LeaseListLoaderResultItem {
  leaseInfo: APITypes.Lease;
  managerInfo?: APITypes.User | APITypes.Organization;
  entityType?: APITypes.EntityType;
  entityDetails?: APITypes.Unit | APITypes.Building;
  application?: APITypes.Application;
}

/** amenity-add-reservation loader response */
export interface AmenityAddReservationLoaderResult {
  amenityData: {
    formattedAmenityDetails: {
      amenityDetails: APITypes.Amenity;
      propertyDetails: APITypes.Property;
      buildingDetails: APITypes.Building;
      reservations: Array<{
        timeFrame: { start: string; end: string };
        createdAt: string;
        data: APITypes.AmenityReservation;
      }>;
    };
    availability: {
      available: Array<Array<{ start: string; end: string }>>;
      unavailable: Array<{
        timeFrame: { start: string; end: string };
        reason?: string;
      }>;
      fullyUnavailable: string | boolean;
    };
  };
}

/** amenity-table loader response (raw - transform on client for dayjs) */
export interface AmenityTableLoaderResult {
  amenityList: Array<{
    formattedAmenityDetails: {
      amenityDetails: APITypes.Amenity;
      propertyDetails: APITypes.Property;
      buildingDetails: APITypes.Building;
      reservations: Array<{
        timeFrame: { start: string; end: string };
        createdAt: string;
        data: APITypes.AmenityReservation;
      }>;
    };
    availability: {
      available: Array<Array<{ start: string; end: string }>>;
      unavailable: Array<{
        timeFrame: { start: string; end: string };
        reason?: string;
      }>;
      fullyUnavailable: string | boolean;
    };
  }>;
}

/** contact-management loader response */
export interface ContactManagementLoaderResult {
  propertyManager: APITypes.User | null;
}

/** message-details loader response */
export interface MessageDetailsLoaderResult {
  message: APITypes.Message;
  user: {
    userID: string;
    tenantID: string;
    tenantName: string;
  };
}

/** message-table loader response */
export interface MessageTableLoaderResult {
  messages: APITypes.Message[];
  user: {
    userID: string;
    tenantID: string;
    tenantName: string;
  };
}

/** my-documents loader response */
export interface MyDocumentsLoaderResult {
  fileList: { name: string; key: string; url: string }[] | null;
}

/** payment loader response */
export interface PaymentLoaderResult {
  lease: APITypes.Lease;
  offer?: APITypes.Offer;
  serviceRequests?: APITypes.ServiceRequest[];
  transactions?: APITypes.Transaction[];
  history?: {
    month: number;
    year: number;
    paid: number;
    complete: boolean;
    expectedAmount?: number;
    periodStart?: string;
    periodEnd?: string;
  }[];
  reason?: APITypes.TransactionReason | string;
  transaction_id: string;
}

/** lease-details loader response */
export interface LeaseDetailsLoaderResult {
  formattedLease: {
    leaseInfo: APITypes.Lease;
    tenantInfo: APITypes.Tenant;
    entityType: APITypes.EntityType;
    entityDetails: APITypes.Unit | APITypes.Building;
    application?: APITypes.Application;
    background?: APITypes.BackgroundCheck;
    offer?: APITypes.Offer;
    moveIn?: APITypes.MoveExam;
    moveOut?: APITypes.MoveExam;
    advanceLeaseAction: _AdvanceLeaseActionType;
    cancelLeaseAction: _CancelLeaseActionType;
    managerInfo?: APITypes.User | APITypes.Organization;
  };
  entityDetails: APITypes.Unit | APITypes.Building;
  userID: string;
  userName: string;
}

/** lease-renewal loader response */
export interface LeaseRenewalLoaderResult {
  tenantInfo: APITypes.Tenant;
  activeLeaseData: LeaseDetailsLoaderResult["formattedLease"];
  nextLeaseData: LeaseDetailsLoaderResult["formattedLease"] | null;
}

export default apiClient;
